import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "../../assets/mit_logo_large.jpg";
import marcus from "../../assets/macus.png";
import { t } from "i18next";

// const CONTRACT_TYPE_CHOICES = [
//   { value: "demo", label: "Demo" },
//   { value: "rental", label: "Rental" },
//   { value: "leasing", label: "Leasing" },
//   { value: "sale", label: "Sale" },
//   { value: "maintenance", label: "Maintenance" },
// ];

const TransportOrderForm = ({ assetData = {}, isExporting = false }) => {
  const { t } = useTranslation();

  const STATUS_CHOICES = useMemo(
    () => [
      {
        value: "Short Time Hire",
        label: t("assets.options.shortTermHire", "Short Time Hire"),
      },
      { value: "New", label: t("assets.options.new", "New") },
      { value: "Used", label: t("assets.options.used", "Used") },
      { value: "Demo", label: t("assets.options.demo", "Demo") },
    ],
    [t]
  );

  const CONTRACT_TYPE_CHOICES = useMemo(
    () => [
      { value: "demo", label: t("assets.options.demo", "Demo") },
      { value: "rental", label: t("assets.options.rental", "Rental") },
      { value: "leasing", label: t("assets.options.leasing", "Leasing") },
      { value: "sale", label: t("assets.options.sale", "Sale") },
      {
        value: "maintenance",
        label: t("assets.options.maintenance", "Maintenance"),
      },
    ],
    [t]
  );

  // Editable state
  const [form, setForm] = useState({
    status: assetData.status || "",
    contract_type: assetData.contracts?.[0]?.contract_type || "",
    warehouse: {
      company: assetData.warehouse?.name || "",
      street: assetData.warehouse?.warehouse_street || "",
      zip_city: [
        assetData.warehouse?.warehouse_zip,
        assetData.warehouse?.warehouse_city,
      ]
        .filter(Boolean)
        .join(" / "),
      city: assetData.warehouse?.warehouse_city || "",
      contact: assetData.warehouse?.name || "",
      phone: "",
    },
    pickup: {
      company: assetData.contracts?.[0]?.customer?.customer_company || "",
      street: assetData.contracts?.[0]?.delivery_address_street || "",
      zip_city: [
        assetData.contracts?.[0]?.delivery_address_zip,
        assetData.contracts?.[0]?.delivery_address_city,
      ]
        .filter(Boolean)
        .join(" / "),
      city: assetData.contracts?.[0]?.delivery_address_city || "",
      contact: assetData.contracts?.[0]?.customer?.name || "",
      phone: assetData.contracts?.[0]?.customer?.phone_number || "",
    },
    delivery: {
      company: assetData.contracts?.[0]?.customer?.customer_company || "",
      street: assetData.contracts?.[0]?.invoice_address_street || "",
      zip_city: [
        assetData.contracts?.[0]?.invoice_address_zip,
        assetData.contracts?.[0]?.invoice_address_city,
      ]
        .filter(Boolean)
        .join(" / "),
      city: assetData.contracts?.[0]?.invoice_address_city || "",
      contact: assetData.contracts?.[0]?.customer?.name || "",
      phone: assetData.contracts?.[0]?.customer?.phone_number || "",
    },
    transportItems: [
      {
        quantity: 1,
        classification: assetData.classification || "",
        type: assetData.type || "",
        serial_no: assetData.serial_no || "",
        machine_length_mm: assetData.machine_length_mm || "",
        machine_width_mm: assetData.machine_width_mm || "",
        machine_height_mm: assetData.machine_height_mm || "",
        machine_weight_kg: assetData.machine_weight_kg || "",
      }
    ],
    manufacturer: assetData.manufacturer || "",
    type: assetData.type || "",
    serial_no: assetData.serial_no || "",
    machine_length_mm: assetData.machine_length_mm || "",
    machine_width_mm: assetData.machine_width_mm || "",
    machine_height_mm: assetData.machine_height_mm || "",
    machine_weight_kg: assetData.machine_weight_kg || "",
    battery: assetData.battery || false,
    date: assetData.date || "",
    order_no: assetData.order_no || "",
    date_of_pick: assetData.date_of_pick || "",
    date_of_delivery: assetData.date_of_delivery || "",
    ramp: assetData.ramp || "",
    ramp_yes: false,
    ramp_no: false,
    battery_yes: false,
    battery_no: false,
    charger_yes: false,
    charger_no: false,
    others: assetData.others || "",
    signature_date: assetData.signature_date || "",
    signature_yes: false,
    signature_no: false,
    notes_line_1: '',  // Add this
    notes_line_2: '',
  });

  // Generic change handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "status" || name === "contract_type") {
      setForm((prev) => ({
        ...prev,
        [name]: checked ? value : "",
      }));
    } else if (
      name.startsWith("pickup.") ||
      name.startsWith("delivery.") ||
      name.startsWith("warehouse.")
    ) {
      const [section, field] = name.split(".");
      setForm((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTransportItemChange = (idx, e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = [...prev.transportItems];
      updated[idx][name] = value;
      return { ...prev, transportItems: updated };
    });
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-1 pb-4">
        {/* Left: Mitsubishi Logo */}
        <div className="flex items-center justify-start w-1/3">
          <img
            src={logo}
            alt="Mitsubishi Logo"
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Center: Titles */}
        <div className="flex flex-col items-center justify-center w-1/3 text-center relative left-8">
          <h1 className="text-2xl font-bold text-gray-800 tracking-wide leading-tight">
            {t("transportOrder.createTransportOrder", "Transport Order")}
          </h1>
        </div>
        <div className="w-1/3"></div>
      </div>

      {/* Status & Contract Type Section */}
      <div className="border-t-2 border-black mb-4">
        <div className="flex flex-row items-center py-4 gap-12 justify-center">
          {/* Status Group */}
          <div className="flex items-center">
            <span className="font-bold mr-4 min-w-[80px]">
              {t("transportOrder.status", "Status")}
            </span>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {STATUS_CHOICES.map((status) => (
                <label
                  key={status.value}
                  className="flex items-center gap-1 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name="status"
                    value={status.value}
                    checked={form.status === status.value}
                    onChange={handleChange}
                    className="w-5 h-5 border-2 border-black mr-1"
                  />
                  <span>
                    {t(`transportOrder.status.${status.value}`, status.label)}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {/* Contract Type Group */}
          <div className="flex items-center">
            <span className="font-bold mr-4 min-w-[110px]">
              {t("transportOrder.contracttype")}
            </span>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {CONTRACT_TYPE_CHOICES.map((ct) => (
                <label
                  key={ct.value}
                  className="flex items-center gap-1 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name="contract_type"
                    value={ct.value}
                    checked={form.contract_type === ct.value}
                    onChange={handleChange}
                    className="w-5 h-5 border-2 border-black mr-1"
                  />
                  <span>
                    {t(`transportOrder.contractType.${ct.value}`, ct.label)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div className="border-[3px] border-black border-t-[3px]">
        <div className="flex">
          {/* Left Side - Pickup Address */}
          <div className="flex-1 border-r-[3px] border-black">
            <div className="px-4 py-2 border-b-[3px] border-black font-bold text-center">
              {t("transportOrder.warehouseAddress", "Warehouse")}
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("transportOrder.company")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="warehouse.company"
                    value={form.warehouse?.company || ""}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.company",
                          "Enter company name"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("wareh.street", "Street")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="warehouse.street"
                    value={form.warehouse?.street || ""}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.street",
                          "Enter street address"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2  border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.zip")} /{t("transportOrder.city")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="warehouse.zip_city"
                    value={form.warehouse?.zip_city || ""}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.zipCity",
                          "ZIP Code / City"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.ContactPerson")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm font-semibold">
                  <input
                    type="text"
                    name="warehouse.contact"
                    value={form.warehouse?.contact || ""}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.contactPerson",
                          "Enter contact person"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.phone", "Phone")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="warehouse.phone"
                    value={form.warehouse?.phone || ""}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.phone",
                          "Enter phone number"
                        )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 border-r-[3px] border-black">
            <div className="px-4 py-2 border-b-[3px] border-black font-bold text-center">
              {t("transportOrder.deliveryaddress")}
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("transportOrder.company")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="pickup.company"
                    value={form.pickup.company}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.company",
                          "Enter company name"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("transportOrder.street", "Street")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="pickup.street"
                    value={form.pickup.street}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.street",
                          "Enter street address"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2  border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.zip")} /{t("transportOrder.city")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="pickup.zip_city"
                    value={form.pickup.zip_city}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.zipCity",
                          "ZIP Code / City"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.ContactPerson")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm font-semibold">
                  <input
                    type="text"
                    name="pickup.contact"
                    value={form.pickup.contact}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.contactPerson",
                          "Enter contact person"
                        )
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.phone", "Phone")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="pickup.phone"
                    value={form.pickup.phone}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                          "transportOrder.placeholders.phone",
                          "Enter phone number"
                        )
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Delivery Address */}
          {/* <div className="flex-1">
            <div className="px-4 py-2 border-b-[3px] border-black font-bold text-center">
              {t("transportOrder.pickup")}
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("transportOrder.company", "Company")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="delivery.company"
                    value={form.delivery.company}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${
                      isExporting
                        ? "border-none outline-none"
                        : "border rounded"
                    }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                            "transportOrder.placeholders.company",
                            "Enter company name"
                          )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm">
                  <span className="relative right-5">
                    {t("transportOrder.street", "Street")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="delivery.street"
                    value={form.delivery.street}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${
                      isExporting
                        ? "border-none outline-none"
                        : "border rounded"
                    }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                            "transportOrder.placeholders.street",
                            "Enter street address"
                          )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2  border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.zip", "ZIP Code")}/
                    {t("transportOrder.city", "City")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="delivery.zip_city"
                    value={form.delivery.zip_city}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${
                      isExporting
                        ? "border-none outline-none"
                        : "border rounded"
                    }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                            "transportOrder.placeholders.zipCity",
                            "ZIP Code / City"
                          )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-b border-black">
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.ContactPerson", "Contact Person")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm font-semibold">
                  <input
                    type="text"
                    name="delivery.contact"
                    value={form.delivery.contact}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${
                      isExporting
                        ? "border-none outline-none"
                        : "border rounded"
                    }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                            "transportOrder.placeholders.contactPerson",
                            "Enter contact person"
                          )
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex">
                <div className="w-32 px-4 py-2 border-r border-black text-sm ">
                  <span className="relative right-5">
                    {t("transportOrder.phone", "Phone")}:
                  </span>
                </div>
                <div className="flex-1 px-4 py-2 text-sm">
                  <input
                    type="text"
                    name="delivery.phone"
                    value={form.delivery.phone}
                    onChange={handleChange}
                    className={`px-1 py-0.5 w-full ${
                      isExporting
                        ? "border-none outline-none"
                        : "border rounded"
                    }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t(
                            "transportOrder.placeholders.phone",
                            "Enter phone number"
                          )
                    }
                  />
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Equipment Table Section */}
      <div className="border-black border-t-0 mt-4">
        <div className="border-b border-black">
          <h3 className="font-bold text-sm">
            {t("transportOrder.transportitem", "Transport Items")}
          </h3>
        </div>
        <table className="w-full border-[3px] border-black">
          <thead>
            <tr className="border-b-[3px] border-black">
              <th className="border border-black px-1 py-2 text-sm text-center w-12">
                {t("transportOrder.quantity")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-22">
                {t("transportOrder.classification", "Classification")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-[128px]">
                {t("transportOrder.typetra", "Type")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-24">
                {t("transportOrder.Serialnumber", "Serial Number")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-5">
                {t("transportOrder.machinelength", "Machine Length")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-5">
                {t("transportOrder.machinewidth", "Machine Width")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-5">
                {t("transportOrder.closedheight", "Closed Height")}
              </th>
              <th className="border border-black px-0.5 py-2 text-sm text-center w-5">
                {t("transportOrder.machineweight", "Machine Weight")}
              </th>
            </tr>
          </thead>
          {/* <tbody>
            <tr>
              <td className="border border-black px-0.5 py-2 text-sm text-center">
                <input type="number" name="quantity" value={1} readOnly className={`w-10 text-center bg-transparent ${isExporting ? "border-none outline-none" : "border rounded"
                }`} />
              </td>
              <td className="border border-black px-0.5 py-2 text-sm">
                <input
                  type="text"
                  name="manufacturer"
                  value={form.manufacturer}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-full items-center ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder={t("transportOrder.forklift", "Forklift")}
                />

              </td>
              <td className="border border-black px-0.5 py-2 text-sm text-center">
                <input
                  type="text"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className={` py-0.5 w-full ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder={t("transportOrder.type", "Type")}
                />

              </td>
              <td className="border border-black px-0.5 py-2 text-sm text-center">
                <input
                  type="text"
                  name="serial_no"
                  value={form.serial_no}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-full ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder={t("transportOrder.serialNumber", "Serial Number")}
                />
              </td>
              <td className="border border-black px-3 py-2 text-sm text-center">
                <input
                  type="text"
                  name="machine_length_mm"
                  value={form.machine_length_mm}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-16 text-center ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder="Length"
                />
              </td>
              <td className="border border-black px-3 py-2 text-sm text-center">
                <input
                  type="text"
                  name="machine_width_mm"
                  value={form.machine_width_mm}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-16 text-center ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder="Bredth"
                />
              </td>
              <td className="border border-black px-3 py-2 text-sm text-center">
                <input
                  type="text"
                  name="machine_height_mm"
                  value={form.machine_height_mm}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-16 text-center ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder="Height"
                />
              </td>
              <td className="border border-black px-3 py-2 text-sm text-center">
                <input
                  type="text"
                  name="machine_weight_kg"
                  value={form.machine_weight_kg}
                  onChange={handleChange}
                  className={`px-1 py-0.5 w-20 text-center ${isExporting ? "border-none outline-none" : "border rounded"
                    }`}
                  placeholder={t("transportOrder.weight", "Weight")}
                />
              </td>
            </tr>
            {[...Array(7)].map((_, index) => (
              <tr key={index}>
                <td className="border border-black px-3 py-2 text-sm h-8"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
              </tr>
            ))}
          </tbody> */}
          <tbody>
            {form.transportItems.map((item, idx) => (
              <tr key={idx}>
                <td className="border border-black px-0.5 py-2 text-sm text-center">
                  <input
                    type="number"
                    name="quantity"
                    value={item.quantity}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`w-10 text-center bg-transparent ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                  />
                </td>
                <td className="border border-black px-0.5 py-2 text-sm">
                  <input
                    type="text"
                    name="classification"
                    value={item.classification}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-full items-center ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.classification", "Classification")
                    }
                  />
                </td>
                <td className="border border-black px-0.5 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="type"
                    value={item.type}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting ? "" : t("transportOrder.typetra", "Type")
                    }
                  />
                </td>
                <td className="border border-black px-0.5 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="serial_no"
                    value={item.serial_no}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-full ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.Serialnumber", "Serial Number")
                    }
                  />
                </td>
                <td className="border border-black px-3 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="machine_length_mm"
                    value={item.machine_length_mm}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-16 text-center ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.machinelength", "Length")
                    }
                  />
                </td>
                <td className="border border-black px-3 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="machine_width_mm"
                    value={item.machine_width_mm}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-16 text-center ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.machinewidth", "Width")
                    }
                  />
                </td>
                <td className="border border-black px-3 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="machine_height_mm"
                    value={item.machine_height_mm}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-16 text-center ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.closedheight", "Height")
                    }
                  />
                </td>
                <td className="border border-black px-3 py-2 text-sm text-center">
                  <input
                    type="text"
                    name="machine_weight_kg"
                    value={item.machine_weight_kg}
                    onChange={(e) => handleTransportItemChange(idx, e)}
                    className={`px-1 py-0.5 w-20 text-center ${isExporting
                      ? "border-none outline-none"
                      : "border rounded"
                      }`}
                    placeholder={
                      isExporting
                        ? ""
                        : t("transportOrder.machineweight", "Weight")
                    }
                  />
                </td>
              </tr>
            ))}
            {/* Empty rows for layout */}
            {[...Array(7 - form.transportItems.length)].map((_, index) => (
              <tr key={index}>
                {/* <td className="border border-black px-3 py-2 text-sm h-8"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td>
                <td className="border border-black px-3 py-2 text-sm"></td> */}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Two editable blank lines */}
        <div className="mt-1 space-y-1">
          <div className="border-b border-black">
            <input
              type="text"
              name="notes_line_1"
              value={form.notes_line_1}
              onChange={handleChange}
              className={`w-full text-sm py-1 ${isExporting ? 'border-none outline-none bg-transparent' : 'px-2'
                }`}
              placeholder={isExporting ? '' : 'Additional information line 1'}
            />
          </div>
          <div className="border-b border-black">
            <input
              type="text"
              name="notes_line_2"
              value={form.notes_line_2}
              onChange={handleChange}
              className={`w-full text-sm py-1 ${isExporting ? 'border-none outline-none bg-transparent' : 'px-2'
                }`}
              placeholder={isExporting ? '' : 'Additional information line 2'}
            />
          </div>
        </div>
      </div>

      {/* Bottom Editable Section */}
      <div className="flex flex-row gap-8 mt-8">
        {/* Left Table */}
        <div className="border-4 border-black">
          <div className="grid grid-cols-2">
            <div className=" px-2 py-1 border-black border-r border-b">
              {t("transportOrder.dateorder", "Date:")}
            </div>
            <input
              type="date"
              className={`px-2 py-1 border-b ${isExporting ? "border-none outline-none" : "border rounded"
                }`}
              name="date"
              value={form.date}
              onChange={handleChange}
              placeholder={
                isExporting ? "" : t("transportOrder.dateorder", "Date:")
              }
            />
            <div className="px-2 py-1 border-black border-r border-b">
              {t("transportOrder.orderNumber", "Order-No.")}
            </div>
            <input
              type="text"
              className={`px-2 py-1 border-b ${isExporting ? "border-none outline-none" : "border rounded"
                }`}
              name="order_no"
              value={form.order_no}
              onChange={handleChange}
              placeholder={
                isExporting ? "" : t("transportOrder.orderNumber", "Order-No.")
              }
            />
            <div className=" px-2 py-1 border-black border-r border-b">
              {t("transportOrder.dateofpickup", "Date of Pick-up")}
            </div>
            <input
              type="date"
              className={`px-2 py-1 border-b ${isExporting ? "border-none outline-none" : "border rounded"
                }`}
              name="date_of_pick"
              value={form.date_of_pick}
              onChange={handleChange}
              placeholder={
                isExporting
                  ? ""
                  : t("transportOrder.dateofpickup", "Date of Pick-up")
              }
            />
            <div className=" px-2 py-1 border-black border-r">
              {t("transportOrder.dateofdelivery", "Date of Delivery")}
            </div>
            <input
              type="date"
              className={`px-2 py-1 ${isExporting ? "border-none outline-none" : "border rounded"
                }`}
              name="date_of_delivery"
              value={form.date_of_delivery}
              onChange={handleChange}
              placeholder={
                isExporting
                  ? ""
                  : t("transportOrder.dateofdelivery", "Date of Delivery")
              }
            />
          </div>
        </div>

        {/* Right Table */}
        <div className="border-2 border-black flex-1">
          <div className="grid grid-cols-2">
            {/* Ramp */}
            <div className="font-bold px-2 py-[9px] border-black border-r border-b">
              {t("transportOrder.ramp", "Rampe")}
            </div>
            <div className="border-black border-b flex items-center justify-center">
              <input
                type="checkbox"
                name="ramp_yes"
                checked={form.ramp_yes}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black mr-1"
              />
              <span className="text-xs">{t("transportOrder.yes", "JA")}</span>
              <input
                type="checkbox"
                name="ramp_no"
                checked={form.ramp_no}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black ml-4 mr-1"
              />
              <span className="text-xs">{t("transportOrder.no", "NEIN")}</span>
            </div>

            {/* Battery */}
            <div className="font-bold px-2 py-1 border-black border-r border-b">
              {t("transportOrder.withbattery", "Mit Batterie:")}
            </div>
            <div className="border-black border-b flex items-center justify-center">
              <input
                type="checkbox"
                name="battery_yes"
                checked={form.battery_yes}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black mr-1"
              />
              <span className="text-xs">{t("transportOrder.yes", "JA")}</span>
              <input
                type="checkbox"
                name="battery_no"
                checked={form.battery_no}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black ml-4 mr-1"
              />
              <span className="text-xs">{t("transportOrder.no", "NEIN")}</span>
            </div>

            {/* Charger */}
            <div className="font-bold px-2 py-1 border-black border-r border-b">
              {t("transportOrder.withcharger", "Mit Ladegerät")}
            </div>
            <div className="border-black border-b flex items-center justify-center">
              <input
                type="checkbox"
                name="charger_yes"
                checked={form.charger_yes}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black mr-1"
              />
              <span className="text-xs">{t("transportOrder.yes", "JA")}</span>
              <input
                type="checkbox"
                name="charger_no"
                checked={form.charger_no}
                onChange={handleChange}
                className="w-5 h-5 border-2 border-black ml-4 mr-1"
              />
              <span className="text-xs">{t("transportOrder.no", "NEIN")}</span>
            </div>

            {/* Others */}
            <div className="font-bold px-2 py-1 border-black border-r">
              {t("transportOrder.others")}
            </div>
            <div className="flex items-center justify-center">
              <input
                type="text"
                name="others"
                value={form.others}
                onChange={handleChange}
                className={`border px-1 py-0.5 rounded w-full ${isExporting ? "border-none outline-none" : "border rounded"
                  }`}
                placeholder={isExporting ? "" : t("transportOrder.others")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Condition Section */}
      <div className="mt-4 flex items-center gap-8">
        <div className="px-2 py-1 mb-1 inline-block">
          {t(
            "transportOrder.textmessage",
            "Asset taken over in technically and visually perfect condition:"
          )}
        </div>
        <div className="flex items-center gap-8 ml-8">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="signature_yes"
              checked={form.signature_yes}
              onChange={handleChange}
              className="w-5 h-5 border-2 border-black"
            />
            <span>{t("transportOrder.yes", "JA")}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="signature_no"
              checked={form.signature_no}
              onChange={handleChange}
              className="w-5 h-5 border-2 border-black"
            />
            <span>{t("transportOrder.no", "NEIN")}</span>
          </label>
        </div>
      </div>

      {/* Signature Section */}
      <div className="mt-4">
        <div>
          {/* <span className="font-bold">{t("transportOrder.signatureDate", "Datum / Unterschrift:")}</span> */}
          <span className="font-bold">
            {t("transportOrder.signatureDateEn")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TransportOrderForm;
